import { identity } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import Select from 'js/components/common/Select';
import { paginate } from 'js/utils/pagination.util';
import React, { useCallback, useMemo, memo } from 'react';

import * as hooks from './hooks';

import style from './Pagination.scss';

export const Pagination = memo(({
    page,
    max,
    totalRecords,
    setPage: setPageFromProps = identity,
    setMaxPage: setMaxPageFromProps = identity,
    showCurrentRage = true,
    showPerPage = true,
    paginationOptions = [],
    componentRef
}) => {
    const t = useTranslations('playtech.mobile.system.pagination.');
    const pagination = useMemo(() => paginate({
        totalItems: totalRecords,
        currentPage: page,
        pageSize: max
    }), [max, page, totalRecords]);
    const scrollToComponentTop = hooks.useScrollToComponentTop(componentRef);

    const setPage = useCallback((item) => {
        setPageFromProps(item);
        scrollToComponentTop();
    }, [setPageFromProps, scrollToComponentTop]);

    const setMaxPage = useCallback((item) => {
        setMaxPageFromProps(item);
        scrollToComponentTop();
    }, [setMaxPageFromProps, scrollToComponentTop]);

    const setNextPage = useCallback(() => {
        const maxPage = Math.max(1, Math.ceil(totalRecords / max));

        setPage(Math.min(maxPage, page + 1));
    }, [setPage, max, page, totalRecords]);

    const setPreviousPage = useCallback(() => {
        setPage(Math.max(1, page - 1));
    }, [setPage, page]);

    return (
        <div className={style.container}>
            {
                showPerPage && paginationOptions.length > 0
                    ?
                    <div className={style.itemsPerPage}>
                        <span className={style.itemsPerPageLabel}>{t('perPage')}</span>
                        <Select
                            value={max}
                            options={paginationOptions}
                            aria-label={t(['perPage', 'playtech.mobile.system.select.aria-label'])}
                            onChange={(event) => setMaxPage(event.target.value)}
                        />
                    </div>
                    :
                    null
            }
            {
                showCurrentRage && totalRecords > 0
                    ?
                    <div className={style.currentRange}>
                        {t('currentRange', [
                            Math.min((page - 1) * max + 1, totalRecords),
                            Math.min(totalRecords, page * max),
                            totalRecords
                        ])}
                    </div>
                    :
                    null
            }
            {
                totalRecords > 0
                    ?
                    <div className={style.pageLinks}>
                        {
                            pagination.startPage < page
                                ?
                                <AccessibilityElement
                                    tagName="span"
                                    onClick={setPreviousPage}
                                    className={`${style.link} ${style.clickableLink}`}
                                >
                                    &lsaquo;
                                </AccessibilityElement>
                                :
                                <span className={style.link}>
                                    &lsaquo;
                                </span>
                        }
                        {
                            pagination.pages
                                .map((pageNumber) =>
                                    pageNumber === page
                                        ?
                                        <span
                                            key={pageNumber}
                                            className={style.link}
                                        >
                                                {pageNumber}
                                            </span>
                                        :
                                        <AccessibilityElement
                                            tagName="span"
                                            key={pageNumber}
                                            onClick={() => setPage(pageNumber)}
                                            className={`${style.link} ${style.clickableLink}`}
                                        >
                                            {pageNumber}
                                        </AccessibilityElement>
                                )
                        }
                        {
                            pagination.endPage > page
                                ?
                                <AccessibilityElement
                                    tagName="span"
                                    onClick={setNextPage}
                                    className={`${style.link} ${style.clickableLink}`}
                                >
                                    &rsaquo;
                                </AccessibilityElement>
                                :
                                <span className={style.link}>
                                    &rsaquo;
                                </span>
                        }
                    </div>
                    :
                    null
            }
        </div>
    );
});
